import { useEffect, useState } from 'react';
import { ModalLayout, Button, TextArea, IconHighlight } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import ModalFooter from '../../components/modals/parts/ModalFooter.jsx';
import { setSurfaceExposure, getSurfaceDisableImpact } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

const DESTRUCTIVE_BTN =
  '!border !border-[rgba(239,68,68,0.2)] !bg-[rgba(239,68,68,0.10)] !text-[#EF4444] hover:!bg-[rgba(239,68,68,0.18)]';

const STATE_CHIP =
  'inline-flex items-center rounded-[7px] border px-[11px] py-[5px] text-xs font-bold leading-none';
const STATE_CURRENT = 'border-edge bg-white/[0.04] text-content-secondary';
const STATE_ON =
  'border-[rgba(16,185,129,0.42)] bg-[rgba(16,185,129,0.10)] text-[#34d39c]';
const STATE_OFF = 'border-edge bg-white/[0.03] text-content-faint';

/**
 * Governed surface-exposure change (CT-4207). Matches the prototype hide/show
 * confirm: tenant-wide subtitle, Available → Disabled pills, consumer impact
 * box, required audit reason, and an outlined "Hide for tenant" confirm.
 *
 * @param {object} props
 * @param {{ slug: string, name: string, surface: string, currentlyOn: boolean }} props.change
 * @param {() => void} props.onClose
 * @param {() => void} props.onApplied
 */
function SurfaceExposureModal({ change, onClose, onApplied }) {
  const { t } = useTranslation('admin-studio');
  const { slug, name, surface, currentlyOn } = change;
  const hiding = currentlyOn;
  const next = !currentlyOn;

  const [reason, setReason] = useState('');
  const [consumers, setConsumers] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyError, setApplyError] = useState(null);

  useEffect(() => {
    if (!hiding) return undefined;
    let cancelled = false;
    getSurfaceDisableImpact(slug, surface)
      .then((res) => {
        if (cancelled) return;
        const body = validateApiResponse(res).body ?? {};
        setConsumers(Array.isArray(body.consumers) ? body.consumers : []);
      })
      .catch(() => {
        if (!cancelled) setConsumers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, surface, hiding]);

  const apply = async () => {
    if (submitting || !reason.trim()) return;
    setSubmitting(true);
    setApplyError(null);
    try {
      await setSurfaceExposure(slug, surface, next, reason.trim());
      onApplied();
    } catch (err) {
      setApplyError(err?.message ?? t('common.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const surfaceLabel = t(`integrations.distribution.columns.${surface}`);
  const title = t(hiding ? 'integrations.distribution.change.hideTitle' : 'integrations.distribution.change.showTitle', {
    name,
    surface: surfaceLabel,
  });

  return (
    <ModalLayout
      isOpen
      onClose={onClose}
      useBodyPortal
      maxWidthClassName="max-w-[520px]"
      title={title}
      subtitle={t(
        hiding ? 'integrations.distribution.change.hideSubtitle' : 'integrations.distribution.change.showSubtitle',
      )}
      titlePrefix={
        hiding ? (
          <IconHighlight
            icon={AlertTriangle}
            size="medium"
            bgColor="Error"
            iconColor="Error"
            className="border border-red-500/40"
          />
        ) : (
          <IconHighlight
            icon={Check}
            size="medium"
            bgColor="Success"
            iconColor="Success"
            className="border border-emerald-500/40"
          />
        )
      }
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className={`${STATE_CHIP} ${STATE_CURRENT}`}>
            {t(hiding ? 'integrations.distribution.available' : 'integrations.distribution.disabled')}
          </span>
          <ArrowRight size={14} className="text-content-faint" aria-hidden="true" />
          <span className={`${STATE_CHIP} ${next ? STATE_ON : STATE_OFF}`}>
            {t(next ? 'integrations.distribution.available' : 'integrations.distribution.disabled')}
          </span>
        </div>

        {hiding ? (
          <div className="overflow-hidden rounded-[10px] border border-edge">
            <p className="border-b border-edge bg-white/[0.02] px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-content-faint">
              {t('integrations.distribution.change.impactHeading', { surface: surfaceLabel })}
            </p>
            {consumers == null ? (
              <p className="px-3.5 py-4 text-[12.5px] text-content-secondary">…</p>
            ) : consumers.length === 0 ? (
              <p className="px-3.5 py-4 text-[12.5px] text-content-secondary">
                {t('integrations.distribution.change.impactEmpty')}
              </p>
            ) : (
              <ul>
                {consumers.map((row) => (
                  <li key={`${row.kind}-${row.label}`} className="flex items-start gap-3 px-3.5 py-[11px]">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#FBBF24]" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-content">{row.label}</p>
                      <p className="text-[11.5px] text-content-faint">
                        {t(`integrations.distribution.change.consumers.desc.${row.descKey}`, {
                          count: row.count,
                          unit: row.unit,
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-content">
            {t('integrations.distribution.change.reasonLabel')}
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              *
            </span>
          </span>
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('integrations.distribution.change.reasonPlaceholder')}
            rows={3}
            required
            aria-required="true"
          />
        </label>
        {applyError ? (
          <p className="text-sm font-medium text-red-600 dark:text-red-400">{applyError}</p>
        ) : null}
      </div>
      <ModalFooter className="!px-6 !py-3.5">
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant={hiding ? 'ghost' : 'primary'}
          size="sm"
          className={hiding ? DESTRUCTIVE_BTN : undefined}
          disabled={!reason.trim() || submitting}
          onClick={apply}
        >
          {t(hiding ? 'integrations.distribution.change.hideConfirm' : 'integrations.distribution.change.showConfirm')}
        </Button>
      </ModalFooter>
    </ModalLayout>
  );
}

export default SurfaceExposureModal;
